import React from "react";
import { Download, Upload, Loader2, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, downloadCSV, formatApiError } from "../../lib/api";

export function ExportCsvButton({ path, filename, testid }) {
  const [busy, setBusy] = React.useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await downloadCSV(path, filename);
      toast.success("Export CSV téléchargé");
    } catch {
      toast.error("Erreur lors de l'export");
    }
    setBusy(false);
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      className="shrink-0 hidden lg:inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:border-slate-400 transition-colors disabled:opacity-50"
      data-testid={testid}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export
    </button>
  );
}

export function ImportCsvButton({ path, onDone, testid }) {
  const [busy, setBusy] = React.useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(path, fd);
      toast.success(`Import terminé : ${data.created} créé(s), ${data.skipped} ignoré(s)`);
      if (data.errors?.length) toast.warning(data.errors.slice(0, 3).join(" · "));
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'import");
    }
    setBusy(false);
  };
  return (
    <label
      className="shrink-0 hidden lg:inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:border-slate-400 transition-colors cursor-pointer"
      data-testid={testid}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Import
      <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} disabled={busy} />
    </label>
  );
}

// Menu compact ⋯ regroupant Export (+ Import si disponible) CSV
export function CsvMenu({ exportPath, exportFilename, importPath = null, onDone, testid = "csv-menu" }) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const runExport = async () => {
    setBusy(true);
    try { await downloadCSV(exportPath, exportFilename); toast.success("Export CSV téléchargé"); }
    catch { toast.error("Erreur lors de l'export"); }
    setBusy(false); setOpen(false);
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(importPath, fd);
      toast.success(`Import terminé : ${data.created} créé(s), ${data.skipped} ignoré(s)`);
      if (data.errors?.length) toast.warning(data.errors.slice(0, 3).join(" · "));
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'import");
    }
    setBusy(false); setOpen(false);
  };
  return (
    <div className="relative shrink-0 hidden lg:block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-slate-200 bg-white text-slate-600 hover:border-slate-400 transition-colors"
        data-testid={testid}
        aria-label="Plus d'actions"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-30 w-44 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden" data-testid={`${testid}-dropdown`}>
          <button
            type="button"
            onClick={runExport}
            disabled={busy}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid={`${testid}-export`}
          >
            <Download className="h-3.5 w-3.5 text-slate-400" /> Exporter (CSV)
          </button>
          {importPath && (
            <label
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer border-t border-slate-100"
              data-testid={`${testid}-import`}
            >
              <Upload className="h-3.5 w-3.5 text-slate-400" /> Importer (CSV)
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} disabled={busy} />
            </label>
          )}
        </div>
      )}
    </div>
  );
}
